import type { PDFFont, PDFPage } from 'pdf-lib'
import PDFOperatorNames from 'pdf-lib/es/core/operators/PDFOperatorNames'

// eslint-disable-next-line
const getOperators = (page: PDFPage) => (page as any).getContentStream().operators as { readonly name: string }[]

const drawTableRow = (
  page: PDFPage,
  x: number,
  y: number,
  columnGap: number,
  record: readonly { readonly text: string; readonly width: number; readonly textAlign?: 'left' | 'right' }[],
) => {
  const operators = getOperators(page)
  const { font, fontSize } = page as any as { font: PDFFont; fontSize: number }
  let operatorsLength = operators.length
  let lineCount = 0
  for (const { text, width: maxWidth, textAlign } of record) {
    let _x = x
    if (textAlign === 'right') {
      const width = font.widthOfTextAtSize(text, fontSize)
      if (width < maxWidth) {
        _x = x + maxWidth - width
      }
    }
    const wordBreaks = [...new Set(text.replace(/\n|\f|\r/g, '').split(''))]
    page.drawText(text, { x: _x, y, maxWidth, wordBreaks })
    x += maxWidth + columnGap
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    lineCount = Math.max(lineCount, operators.slice(operatorsLength).filter(op => op.name === PDFOperatorNames.NextLine).length)
    operatorsLength = operators.length
  }
  return { lineCount }
}

export interface PdfTableColumn {
  readonly header: { readonly text: string }
  readonly width: number
  readonly textAlign?: 'left' | 'right'
}

export interface PdfTableOptions {
  readonly pageFactory: () => PDFPage
  readonly margin: { readonly x: number; readonly y: number }
  readonly gap: { readonly row: number; readonly column: number }
  readonly columns: PdfTableColumn[]
}

export class PdfTable {
  private readonly pageFactory: () => PDFPage
  private readonly margin: { readonly x: number; readonly y: number }
  private readonly gap: { readonly row: number; readonly column: number }
  private readonly columns: readonly PdfTableColumn[]
  private currentPage: PDFPage
  private currentY: number

  private get lineHeight() {
    return (this.currentPage as any as { lineHeight: number }).lineHeight
  }

  constructor({ pageFactory, columns, margin, gap }: PdfTableOptions) {
    this.pageFactory = pageFactory
    this.margin = { ...margin }
    this.gap = { ...gap }
    this.currentPage = this.pageFactory()
    this.currentY = this.currentPage.getHeight() - this.margin.y - this.lineHeight
    // width: -1 の列を width: (ページ幅 - 余白 - 他の列の幅の合計) にします。
    this.columns = columns.map(c => ({
      ...c,
      width:
        c.width === -1
          ? this.currentPage.getWidth() - this.margin.x * 2 - columns.reduce((w, c) => w + c.width + this.gap.column, 1)
          : c.width,
    }))
    this.appendHeaderRow()
  }

  private appendHeaderRow() {
    this.drawHorizontalLine(this.currentY - 2 - this.gap.row)
    this.drawHorizontalLine(this.currentY - 4 - this.gap.row)
    this.appendRow(this.columns.map(c => c.header.text))
    this.currentY -= 4 + this.gap.row
  }

  private drawHorizontalLine(y: number) {
    this.currentPage.drawLine({
      start: { x: this.margin.x, y },
      end: { x: this.currentPage.getWidth() - this.margin.x, y },
      thickness: 0.75,
      lineCap: 1,
    })
  }

  appendRow(record: readonly string[]) {
    const operators = getOperators(this.currentPage)
    const operatorsLength = operators.length

    this.rawAppendRow(record)

    if (this.currentY < this.margin.y) {
      operators.splice(operatorsLength)
      this.currentPage = this.pageFactory()
      this.currentY = this.currentPage.getHeight() - this.margin.y - this.lineHeight
      this.appendHeaderRow()
      this.rawAppendRow(record)
    }
  }

  private rawAppendRow(record: readonly string[]) {
    const { lineCount } = drawTableRow(
      this.currentPage,
      this.margin.x + this.gap.column,
      this.currentY,
      this.gap.column,
      this.columns.map(({ width, textAlign }, i) => ({ text: record[i], width, textAlign })),
    )
    this.currentY -= lineCount * this.lineHeight + this.gap.row
  }
}
